/*
 * CONFIDENTIAL AND PROPRIETARY
 *
 * Copyright (c) 2003 - 2021 Levels Beyond
 * Copyright (c) 2021 - 2023 Signiant (by acquisition)
 * Portions Copyright (c) 2023 - 2026 Quickplay Media
 *
 * ALL RIGHTS RESERVED
 *
 * This software is licensed to Quickplay Media under a formal
 * license agreement. Unauthorized use, reproduction, or
 * distribution is strictly prohibited.
 */

function TranslatableMetadataController(
    this: JS5Object,
    _: JS5Object,
    $q: JS5Object,
    LanguagesService: JS5Object,
    TranslatableMetadataService: JS5Object,
) {

    //=======================
    // Internal Functions
    //=======================

    let getSortedLanguageList = (allLanguages: JS5Object[], dataObject: JS5Object) => {
        // if the dataObject has an `originalLanguage` property,
        // that will always be first
        let originalLanguage = _.find(allLanguages, {id: _.get(dataObject, 'originalLanguage.id')});

        // find the root translatable text language
        let rootLanguage = _.find(allLanguages, {id: _.get(dataObject, 'translatableText.language.id')});

        // find the default language in the system
        let defaultLanguage = _.find(allLanguages, {isDefault: true});

        // find all translated languages in the data object and sort them by name
        let translatedLanguages = _.chain(dataObject)
            .get('translatableText.translations')
            .map((translation: JS5Object) => _.find(allLanguages, {id: translation.language.id}))
            .sortBy('name')
            .value();

        // make an ordered list of all used languages
        let usedLanguages: number[] = [];
        if (_.get(originalLanguage, 'id')) {
            usedLanguages.push(originalLanguage.id);
        }
        if (_.get(rootLanguage, 'id') && _.get(originalLanguage, 'id') && rootLanguage.id !== originalLanguage.id) {
            usedLanguages.push(rootLanguage.id);
        }
        if (!_.includes(usedLanguages, defaultLanguage.id)) {
            usedLanguages.push(defaultLanguage.id);
        }
        usedLanguages.push(..._.map(translatedLanguages, 'id'));

        // make another list of all unused languages
        let unusedLanguages = _.chain(allLanguages)
                .filter((language: JS5Object) => !_.includes(usedLanguages, language.id))
                .map('id')
                .value();

        let languages: JS5Object[] = [];
        languages.push(..._.map(usedLanguages, (language: JS5Object) => _.find(allLanguages, {id: language})));
        languages.push(..._.map(unusedLanguages, (language: JS5Object) => _.find(allLanguages, {id: language})));

        return languages;
   }

   let getTranslationForLanguage = (languageId: number) => {
        return _.find(
            this.dataObject.translatableText.translations,
            { language: { id: languageId } }
        );
   }

    let createTranslationForLanguage = (languageId: number) => {
        let translation: JS5Object = {
            language: {
                id: languageId,
                aliasCount: 0
            }
        };

        _.forEach(this.fields, (field: JS5Object) => {
            translation[field.name] = '';
        });
        if (!_.get(this, 'dataObject.translatableText.translations')) {
            this.dataObject.translatableText.translations = [];
        }
        this.dataObject.translatableText.translations.push(translation);

        return getTranslationForLanguage(languageId);
    }

    let setSelectedLanguageTranslation = () => {
        let selectedLanguageId = _.get(this, 'selectedLanguage.id');

        if (selectedLanguageId === this.dataObject.translatableText.language.id) {
            // edit the root translatable
            this.translation = this.dataObject.translatableText;
        } else {
            // edit one of the translations
            this.translation = getTranslationForLanguage(selectedLanguageId);

            // if the translation doesn't exist, create it and
            // edit the created one
            if (!this.translation) {
                this.translation = createTranslationForLanguage(selectedLanguageId);
            }
        }
    }

    let captureDataObject = () => {
        return $q.all({
            languages: LanguagesService.getAllLanguages(),
            dataObject: TranslatableMetadataService.dataObjectPromise,
        }).then((data: JS5Object) => {

            this.languages = getSortedLanguageList(data.languages, data.dataObject);
            this.dataObject = data.dataObject;

            // initialize translatable text object if it doesn't
            // exist
            if (!_.get(this, 'dataObject.translatableText')) {
                this.dataObject.translatableText = {
                    language: this.languages[0],
                    translations: [],
                }
            }
        });
    }


    //===============//
    // Service Hooks //
    //===============//

    let preSave = () => {
        console.log(`preSave() hook called`);
        _.remove(this.dataObject.translatableText.translations, (translation: JS5Object) => {
            let fields = ['value', 'title', 'shortDescription', 'longDescription', 'keywords'];
            // return _.every(fields, (field: JS5Object) => !_.get(translation, field, undefined));
            let shouldRemove = _.every(fields, (field: JS5Object) => !_.get(translation, field, undefined));
            let translationName = _.find(this.languages, {id: translation.language.id}).name;
            if (shouldRemove) {
                console.log(`removing translation: ${translationName}`);
            } else {
                console.log(`not removing translation: ${translationName}`);
            }
            return shouldRemove;
        });
    }

    let onSave = () => {
        let selectedLanguageId = _.get(this, 'selectedLanguage.id', this.languages[0]);
        captureDataObject().then(() => {
            this.selectedLanguage = _.find(this.languages, {id: selectedLanguageId});
            setSelectedLanguageTranslation();
        });
    };

    let onRevert = () => {
        captureDataObject().then(() => {
            this.selectedLanguage = this.languages[0];
            setSelectedLanguageTranslation();
        });
    };


    //=======================
    // Template Logic
    //=======================

    this.isFieldEnabled = (name: string) => {
        return _.find(this.fields, {name}) != undefined;
    }

    this.isFieldRequired = (name: string) => {
        const field = _.find(this.fields, {name});
        return _.get(field, 'required', false);
    }

    this.getFieldTitle = (name: string) => {
        return _.get(_.find(this.fields, {name}), 'title', 'undefined');
    }

    this.getFieldMaxChars = (name: string) => {
        const field = _.find(this.fields, {name});
        return _.get(field, 'maxChars', -1);
    }

    this.onLanguageChange = () => {
        setSelectedLanguageTranslation();
    }

    this.clearAllFields = () => {
        this.editorForm.$setDirty();
        _.forEach(this.fields, (field: JS5Object) => {
            this.translation[field.name] = '';
        });
    }


    //=======================
    // Lifecycle Hooks
    //=======================

    this.$onDestroy = () => {
        TranslatableMetadataService.resetDataObjectPromise();
    }

    this.$onInit = () => {
        // binding defaults
        this.formTitle = this.formTitle || 'METADATA';

        TranslatableMetadataService.preSave = preSave;
        TranslatableMetadataService.onSave = onSave;
        TranslatableMetadataService.onRevert = onRevert;

        captureDataObject().then(() => {
            this.selectedLanguage = this.languages[0];
            setSelectedLanguageTranslation();
        });
    }

}

angular
    .module('prism-ui.components')
    .component('translatableMetadata',{
        templateUrl: 'components/visual/translatableMetadata/translatable-metadata-template.html',
        controller: TranslatableMetadataController,
        bindings: {
            fields: '<',
            formTitle: '<?',
        },
    });
